import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = ["all", "Wellness", "Body", "Facial", "Skin"];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const filteredServices = activeCategory === "all" 
    ? services 
    : services.filter(s => s.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#FFFCF9]" data-testid="services-page">
      {/* Hero */}
      <section className="py-20 bg-spa-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Our Services
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-spa-neutral-800 mt-4 mb-6">
              Treatments & Services
            </h1>
            <p className="text-spa-neutral-500 text-lg max-w-2xl mx-auto">
              Discover our comprehensive range of wellness and beauty treatments designed to help you relax, refresh, and rejuvenate.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white sticky top-20 z-40 border-b border-spa-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-medium text-sm transition-all ${
                  activeCategory === category
                    ? "bg-spa-pink text-white"
                    : "bg-spa-neutral-100 text-spa-neutral-600 hover:bg-spa-pink-100"
                }`}
                data-testid={`category-${category}`}
              >
                {category === "all" ? "All Services" : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse bg-spa-neutral-100 rounded-2xl h-80" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, idx) => (
                <Card key={service.id} className="service-card group overflow-hidden rounded-2xl border-0 bg-white spa-shadow" data-testid={`service-item-${idx}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image_url || "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400"}
                      alt={service.name}
                      className="service-image w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/50 to-transparent" />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-spa-pink-500 text-xs font-medium px-3 py-1 rounded-full">
                      {service.category}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-2">
                      {service.name}
                    </h3>
                    <p className="text-spa-neutral-500 text-sm mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-spa-neutral-400">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">{service.duration} min</span>
                      </div>
                      <Link to="/booking">
                        <Button size="sm" className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg text-sm">
                          Book Now
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredServices.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-spa-neutral-500 text-lg">No services found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-spa-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-spa-neutral-800 mb-4">
            Not Sure Which Treatment is Right for You?
          </h2>
          <p className="text-spa-neutral-500 text-lg mb-8">
            Contact us via WhatsApp and our experts will help you choose the perfect treatment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" data-testid="services-book-now">
              <Button className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg px-8 py-6 text-base font-medium">
                Book Appointment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="rounded-lg px-8 py-6 text-base font-medium border-2 border-spa-pink-300 text-spa-pink-600 hover:bg-spa-pink-50">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
